import { useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { Label } from "@/app/admin/_common/ui/label";
import { Input } from "@/app/admin/_common/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/admin/_common/ui/popover";
import ShowThumbnail from "@/app/admin/_common/ShowThumbnail";
import FestivalService, { UpcomingShowOption } from "@/app/_services/FestivalService";
import { formatUtcDate, formatShowDateTime } from "@/app/_utils/dayjs";
import { cn } from "@/app/_utils/cn";
import { Plus, X, Search } from "lucide-react";

export function ShowPicker({
  control,
  errors,
  initialShows = [],
}: {
  control: any;
  errors: any;
  initialShows?: UpcomingShowOption[];
}) {
  const [options, setOptions] = useState<UpcomingShowOption[]>([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    FestivalService.getUpcomingShowsAsync().then(setOptions).catch(() => setOptions([]));
  }, []);

  const optionsById = useMemo(() => {
    const map = new Map<string, UpcomingShowOption>();
    // Already-attached shows may fall outside the "upcoming" set (e.g. past shows) —
    // seed from initialShows first so their titles still render.
    initialShows.forEach((o) => map.set(o.id, o));
    options.forEach((o) => map.set(o.id, o));
    return map;
  }, [options, initialShows]);

  return (
    <Controller
      name="show_ids"
      control={control}
      render={({ field }) => {
        const selectedIds: string[] = field.value ?? [];

        const availableOptions = options.filter(
          (o) =>
            !selectedIds.includes(o.id) &&
            (!query.trim() || o.title.toLowerCase().includes(query.trim().toLowerCase()))
        );

        function addShow(id: string) {
          field.onChange([...selectedIds, id]);
          setQuery("");
        }

        function removeShow(id: string) {
          field.onChange(selectedIds.filter((s) => s !== id));
        }

        return (
          <div className="grid gap-3">
            <div className="flex items-center justify-between">
              <Label>Shows</Label>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex h-[26px] items-center gap-1 rounded-md border border-input bg-white px-2 text-[12px] text-foreground hover:bg-muted"
                  >
                    <Plus className="size-3.5" />
                    Add show
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[320px] p-2">
                  <div className="relative mb-2">
                    <div className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center">
                      <Search className="size-3.5 text-muted-foreground" />
                    </div>
                    <Input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search shows…"
                      className="h-[30px] pl-8 text-[12.5px]"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-[240px] overflow-y-auto">
                    {availableOptions.length === 0 ? (
                      <div className="px-2 py-3 text-center text-[12.5px] text-muted-foreground">
                        No shows found
                      </div>
                    ) : (
                      availableOptions.map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => addShow(o.id)}
                          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-muted"
                        >
                          <ShowThumbnail
                            src={o.img_url}
                            alt={o.title}
                            style={{ height: 28, width: 28, borderRadius: 6, flexShrink: 0 }}
                          />
                          <span className="flex min-w-0 flex-col items-start">
                            <span className="truncate text-foreground">{o.title}</span>
                            <span className="text-[11px] text-muted-foreground">
                              {formatUtcDate(o.start)}
                            </span>
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {selectedIds.length === 0 ? (
              <div
                className={cn(
                  "flex h-[70px] items-center justify-center rounded-md border border-dashed border-border text-[12.5px] text-muted-foreground"
                )}
              >
                No shows added yet
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {selectedIds.map((id) => {
                  const show = optionsById.get(id);
                  return (
                    <div
                      key={id}
                      className="flex items-center gap-3 rounded-md border border-border px-3 py-2.5"
                    >
                      <ShowThumbnail
                        src={show?.img_url}
                        alt={show?.title ?? id}
                        style={{ height: 38, width: 38, borderRadius: 8, flexShrink: 0 }}
                      />
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="truncate text-[12.5px] text-foreground">
                          {show?.title ?? id}
                        </span>
                        {show?.start && (
                          <span className="truncate text-[11px] text-muted-foreground">
                            {formatShowDateTime(show.start, "MMM D, YYYY · h:mm a")}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeShow(id)}
                        className="flex size-5 flex-none items-center justify-center rounded text-muted-foreground hover:bg-muted"
                        aria-label="Remove"
                      >
                        <X className="size-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {errors.show_ids && (
              <span className="text-[12px] text-red-600">{errors.show_ids?.message}</span>
            )}
          </div>
        );
      }}
    />
  );
}
